import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  SxProps,
  Theme,
} from "@mui/material";
import dayjs from "dayjs";
import { FormikProps, getIn } from "formik";
import { ReactNode } from "react";
import CustomDatePicker from "@/components/CustomDatePicker";
import Flex from "@/components/Flex";
import Input from "@/components/Input";
import MaskInput from "@/components/MaskInput";
import Text from "@/components/Text";
import {
  CCM_CONSENT_STYLE,
  CONSENT_DATE_VALUE_FORMAT,
  CONSENT_FIELD_TYPE,
  PHONE_MASK,
} from "../ccm-consent-constant";
import { ConsentFieldConfig, ConsentTextBlock } from "../ccm-consent-types";
import { useConsentReadOnly } from "../hooks/use-consent-form-sync";

export const getConsentFieldError = <T,>(
  formik: FormikProps<T>,
  name: string
): string | undefined =>
  getIn(formik.touched, name) ? getIn(formik.errors, name) : undefined;

export interface ConsentSectionProps {
  title: ReactNode;
  children: ReactNode;
  sx?: SxProps<Theme>;
}

export const ConsentSection = ({
  title,
  children,
  sx,
}: ConsentSectionProps) => (
  <Flex
    component="section"
    flexDirection="column"
    sx={[
      {
        border: 1,
        borderColor: CCM_CONSENT_STYLE.BORDER,
        borderRadius: 1,
        overflow: "hidden",
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    <Box
      px={1.5}
      py={0.75}
      sx={{
        bgcolor: CCM_CONSENT_STYLE.HEADER_BG,
        borderBottom: 1,
        borderColor: CCM_CONSENT_STYLE.BORDER,
      }}
    >
      <Text
        component="h3"
        textAlign="center"
        fontWeight={CCM_CONSENT_STYLE.HEADING_WEIGHT}
        color={CCM_CONSENT_STYLE.HEADER_TEXT}
      >
        {title}
      </Text>
    </Box>
    <Flex flexDirection="column" gap={1.5} p={1.5} flex={1}>
      {children}
    </Flex>
  </Flex>
);

export interface ConsentCheckboxProps {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  isDisabled?: boolean;
}

export const ConsentCheckbox = ({
  label,
  checked,
  onChange,
  isDisabled,
}: ConsentCheckboxProps) => {
  const isReadOnly = useConsentReadOnly();

  return (
    <FormControlLabel
      label={<Text>{label}</Text>}
      disabled={isDisabled || isReadOnly}
      control={
        <Checkbox
          size="small"
          checked={checked}
          onChange={(_, isChecked) => onChange(isChecked)}
        />
      }
    />
  );
};

export interface ConsentTextBlocksProps {
  blocks: readonly ConsentTextBlock[];
}

export const ConsentTextBlocks = ({ blocks }: ConsentTextBlocksProps) => (
  <Flex flexDirection="column" gap={1.5}>
    {blocks.map((block) => (
      <Flex key={block.id} flexDirection="column" gap={0.75}>
        {block.heading && (
          <Text
            fontWeight={CCM_CONSENT_STYLE.HEADING_WEIGHT}
            color={CCM_CONSENT_STYLE.SUB_HEADING}
          >
            {block.heading}
          </Text>
        )}
        {block.lead && (
          <Text fontWeight={CCM_CONSENT_STYLE.HEADING_WEIGHT}>
            {block.lead}
          </Text>
        )}
        {block.paragraphs?.map((paragraph) => (
          <Text key={paragraph} textAlign="justify">
            {paragraph}
          </Text>
        ))}
        {!!block.bullets?.length && (
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {block.bullets.map((bullet) => (
              <Text key={bullet} component="li" textAlign="justify">
                {bullet}
              </Text>
            ))}
          </Box>
        )}
      </Flex>
    ))}
  </Flex>
);

export interface ConsentLinesProps {
  lines: readonly string[];
}

export const ConsentLines = ({ lines }: ConsentLinesProps) => (
  <Flex flexDirection="column">
    {lines.map((line) => (
      <Text key={line}>{line}</Text>
    ))}
  </Flex>
);

export interface ConsentFieldProps<T> {
  formik: FormikProps<T>;
  field: ConsentFieldConfig;
  disabled?: boolean;
}

export const ConsentField = <T,>({
  formik,
  field,
  disabled: isDisabled,
}: ConsentFieldProps<T>) => {
  const isReadOnly = useConsentReadOnly();
  const { name, label, type, required, allowFutureDate } = field;
  const disabled = isDisabled || field.isDisabled || isReadOnly;
  const error = getConsentFieldError(formik, name);
  const fieldProps = formik.getFieldProps<string | null>(name);

  if (type === CONSENT_FIELD_TYPE.DATE) {
    return (
      <CustomDatePicker
        label={label}
        name={name}
        required={required}
        disabled={disabled}
        error={error}
        maxDate={allowFutureDate ? undefined : dayjs()}
        value={fieldProps.value ? dayjs(fieldProps.value) : null}
        handleBlur={formik.handleBlur}
        onChange={(date) => {
          formik.setFieldTouched(name, true, false);
          formik.setFieldValue(
            name,
            date?.isValid() ? date.format(CONSENT_DATE_VALUE_FORMAT) : null
          );
        }}
      />
    );
  }

  if (type === CONSENT_FIELD_TYPE.PHONE) {
    return (
      <MaskInput
        mask={PHONE_MASK}
        label={label}
        name={name}
        required={required}
        disabled={disabled}
        error={error}
        value={fieldProps.value ?? ""}
        onChange={fieldProps.onChange}
        onBlur={fieldProps.onBlur}
      />
    );
  }

  return (
    <Input
      label={label}
      required={required}
      error={error}
      textFieldProps={{
        ...fieldProps,
        value: fieldProps.value ?? "",
        disabled,
        fullWidth: true,
      }}
    />
  );
};

export interface ConsentFieldGridProps<T> {
  formik: FormikProps<T>;
  fields: ConsentFieldConfig[];
}

export const ConsentFieldGrid = <T,>({
  formik,
  fields,
}: ConsentFieldGridProps<T>) => (
  <Grid container spacing={2}>
    {fields.map((field) => (
      <Grid key={field.name} item xs={12} sm={field.sm}>
        <ConsentField formik={formik} field={field} />
      </Grid>
    ))}
  </Grid>
);
